import { useEffect } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { X, Trash2, ShoppingBag, Plus, Minus } from "lucide-react";
import { useCart } from "@/lib/cart";
import { formatBRL } from "@/lib/format";

export function CartDrawer() {
  const { items, isDrawerOpen, closeDrawer, updateQuantity, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isDrawerOpen]);

  if (!isDrawerOpen) return null;

  const total = subtotal();

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/60" onClick={closeDrawer} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Seu carrinho"
        className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-4">
          <h2 className="font-display text-lg font-bold text-eg-dark">Seu carrinho</h2>
          <button
            type="button"
            onClick={closeDrawer}
            aria-label="Fechar"
            className="rounded p-1 hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {items.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
              <ShoppingBag className="h-12 w-12 text-muted-foreground" />
              <p className="text-muted-foreground">Seu carrinho está vazio</p>
              <button
                type="button"
                onClick={closeDrawer}
                className="rounded-md bg-eg-gold px-4 py-2 text-sm font-semibold text-eg-dark hover:bg-eg-goldhover"
              >
                Ver Produtos
              </button>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li key={item.productId} className="flex gap-3">
                  {item.image && (
                    <img
                      src={item.image}
                      alt={item.name}
                      loading="lazy"
                      className="h-16 w-16 rounded object-cover flex-shrink-0"
                    />
                  )}
                  <div className="flex flex-1 flex-col">
                    <p className="text-sm font-medium text-eg-dark line-clamp-2">{item.name}</p>
                    <p className="text-xs text-muted-foreground">{formatBRL(item.price)}</p>
                    <div className="mt-1 flex items-center justify-between">
                      <div className="flex items-center gap-1 rounded border border-border">
                        <button
                          type="button"
                          className="p-1 hover:bg-muted"
                          onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                          aria-label="Diminuir"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="min-w-[2rem] text-center text-sm">{item.quantity}</span>
                        <button
                          type="button"
                          className="p-1 hover:bg-muted"
                          onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                          aria-label="Aumentar"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      <span className="text-sm font-semibold text-eg-dark">
                        {formatBRL(item.price * item.quantity)}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(item.productId)}
                    aria-label="Remover"
                    className="self-start p-1 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-border px-4 py-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-semibold">{formatBRL(total)}</span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Frete</span>
              <span>A calcular no checkout</span>
            </div>
            <div className="flex gap-2">
              <Link
                to="/carrinho"
                onClick={closeDrawer}
                className="flex-1 rounded-md border border-eg-dark px-3 py-2 text-center text-sm font-medium text-eg-dark hover:bg-eg-dark hover:text-white transition-colors"
              >
                Ver Carrinho
              </Link>
              <button
                type="button"
                onClick={() => {
                  closeDrawer();
                  navigate({ to: "/checkout" });
                }}
                className="flex-1 rounded-md bg-eg-gold px-3 py-2 text-sm font-semibold text-eg-dark hover:bg-eg-goldhover transition-colors"
              >
                Finalizar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
